import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  LinearProgress,
  Select,
  MenuItem,
} from "@mui/material";
import createProspectEvent from "api/canprospect/createProspectEvent";
import getProspectHistoryEventTypes from "api/canprospect/getProspectHistoryEventTypes";

interface ProspectHistoryEventType {
  Id: number;
  Type: string;
}

interface AddEventModalProps {
  open: boolean;
  onClose: () => void;
  prospectId: number | null;
  prospectStatusId: number | null;
}

const AddEventModal: React.FC<AddEventModalProps> = ({
  open,
  onClose,
  prospectId,
  prospectStatusId,
}) => {
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [eventTypes, setEventTypes] = useState<ProspectHistoryEventType[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<number>(0);

  useEffect(() => {
    async function fetchEventTypes() {
      try {
        const { eventTypes: fetchedEventTypes } =
          await getProspectHistoryEventTypes();
        setEventTypes(fetchedEventTypes);
        if (fetchedEventTypes.length > 0) {
          // Set the default selected event type to the first type in the list.
          setSelectedEventType(fetchedEventTypes[0].Id);
        }
      } catch (error) {
        console.error("Error fetching event types:", error);
      }
    }
    fetchEventTypes();
  }, []);

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(event.target.value);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const handleSave = async () => {
    if (!prospectId || !prospectStatusId) {
      console.error("Invalid prospect ID or status ID");
      return;
    }

    const payload = {
      Notes: notes.trim() === "" ? "-" : notes, // Pass "-" if notes are empty
      Date: new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }), // Format: "Dec 9 2024 3:54PM"
      ProspectId: prospectId,
      ProspectStatusId: prospectStatusId,
      ProspectHistoryEventTypeId: selectedEventType,
      Consultants: [], // Empty array as default
    };

    try {
      setIsSaving(true);
      await createProspectEvent(payload);
      console.log("Event created successfully");
      setToastOpen(true); // Show toast on successful save
      onClose(); // Close the modal after successful save
    } catch (error) {
      console.error("Error creating event:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Add New Event</DialogTitle>
        <DialogContent style={{ paddingTop: "5px", width: "600px" }}>
          <Box display="flex" flexDirection="column" gap="1.5rem">
            <TextField
              label="Notes"
              value={notes}
              onChange={handleNotesChange}
              multiline
              rows={8}
              fullWidth
              variant="outlined"
            />
            <Box display="flex" justifyContent="flex-start">
              <Select
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(Number(e.target.value))}
                size="small"
              >
                {eventTypes.map((type: ProspectHistoryEventType) => (
                  <MenuItem key={type.Id} value={type.Id}>
                    {type.Type}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </DialogContent>
        {isSaving && <LinearProgress />}{" "}
        <DialogActions style={{ marginBottom: "1rem", marginRight: "10px" }}>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button onClick={onClose} color="secondary" disabled={isSaving}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleToastClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          New Event Created
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddEventModal;
